import {
	keymap,
	highlightSpecialChars,
	drawSelection,
	highlightActiveLine,
	dropCursor,
	rectangularSelection,
	crosshairCursor,
	lineNumbers,
	highlightActiveLineGutter,
	EditorView,
} from '@codemirror/view';
import {EditorState, Compartment} from '@codemirror/state';
import {
	indentOnInput,
	bracketMatching,
	foldGutter,
	foldKeymap,
	defaultHighlightStyle,
	syntaxHighlighting,
} from '@codemirror/language';
import {defaultKeymap, history, historyKeymap} from '@codemirror/commands';
import {searchKeymap, highlightSelectionMatches} from '@codemirror/search';
import {autocompletion, completionKeymap, closeBrackets, closeBracketsKeymap} from '@codemirror/autocomplete';
import {lintKeymap} from '@codemirror/lint';
import {oneDarkTheme, oneDarkHighlightStyle} from '@codemirror/theme-one-dark';
import {html} from '@codemirror/lang-html';
import {javascript} from '@codemirror/lang-javascript';
import {css} from '@codemirror/lang-css';
import {java} from '@codemirror/lang-java';

const lightTheme = EditorView.baseTheme({});
const lightHighlightStyle = defaultHighlightStyle;
const lightHighlighting = syntaxHighlighting(lightHighlightStyle);
const darkTheme = oneDarkTheme;
const darkHighlighting = syntaxHighlighting(oneDarkHighlightStyle);

export default class CodeMirror {
	#editor = null;
	#editableConf = null;
	#themeConf = null;
	#syntaxConf = null;
	#editableExtensionsConf = null;
	#editableExtensions = null;

	constructor({
		parentElement,
		language = CodeMirror.Language.HTML,
		data = '',
		isReadOnly = false,
		onDataChange = () => {},
		showLineNumbers = true,
		lineWrapping = false,
		theme = CodeMirror.Theme.LIGHT,
	}) {
		this.#editableConf = new Compartment();
		this.#themeConf = new Compartment();
		this.#syntaxConf = new Compartment();
		this.#editableExtensionsConf = new Compartment();
		this.#editableExtensions = [
			drawSelection(),
			highlightActiveLine(),
			highlightSelectionMatches(),
			highlightActiveLineGutter(),
			highlightSpecialChars(),
			history(),
			indentOnInput(),
			bracketMatching(),
			closeBrackets(),
			autocompletion(),
			rectangularSelection(),
			crosshairCursor(),
			dropCursor(),
			keymap.of([
				...closeBracketsKeymap,
				...defaultKeymap,
				...searchKeymap,
				...historyKeymap,
				...foldKeymap,
				...completionKeymap,
				...lintKeymap,
			]),
			EditorState.allowMultipleSelections.of(true),
			EditorView.updateListener.of((v) => {
				if (v.docChanged) {
					onDataChange(v.state.doc.toString());
				}
			}),
		];

		const extensions = [
			this.#editableConf.of(EditorView.editable.of(!isReadOnly)),
			this.#themeConf.of(theme === CodeMirror.Theme.DARK ? darkTheme : lightTheme),
			this.#syntaxConf.of(theme === CodeMirror.Theme.DARK ? darkHighlighting : lightHighlighting),
			this.#editableExtensionsConf.of(isReadOnly ? [] : this.#editableExtensions),
		];

		if (showLineNumbers) {
			extensions.push(lineNumbers(), foldGutter());
		}

		if (lineWrapping) {
			extensions.push(EditorView.lineWrapping);
		}

		switch (language) {
			case CodeMirror.Language.TEXT:
				break;
			case CodeMirror.Language.HTML:
				extensions.push(html());
				break;
			case CodeMirror.Language.JAVASCRIPT:
				extensions.push(javascript({jsx: true}));
				break;
			case CodeMirror.Language.CSS:
				extensions.push(css());
				break;
			case CodeMirror.Language.JAVA:
				extensions.push(java());
				break;
		}

		this.#editor = new EditorView({
			state: EditorState.create({
				doc: data,
				extensions,
			}),
			parent: parentElement,
		});
	}

	get contentDOM() {
		return this.#editor.contentDOM;
	}

	setData(data) {
		this.#editor.dispatch({
			changes: {from: 0, to: this.#editor.state.doc.length, insert: data},
		});
	}

	getData() {
		return this.#editor.state.doc.toString();
	}

	insertData(data) {
		const range = this.#editor.state.selection.ranges[0];
		this.#editor.dispatch({
			changes: {from: range.from, to: range.to, insert: data},
		});
	}

	setReadOnly(isReadOnly) {
		this.#editor.dispatch(
			{
				effects: this.#editableConf.reconfigure(EditorView.editable.of(!isReadOnly)),
			},
			{
				effects: this.#editableExtensionsConf.reconfigure(isReadOnly ? [] : this.#editableExtensions),
			},
		);
	}

	getReadOnly() {
		return this.#editor.state.readOnly;
	}

	setTheme(theme) {
		const isDark = theme === CodeMirror.Theme.DARK;
		this.#editor.dispatch(
			{
				effects: this.#themeConf.reconfigure(isDark ? darkTheme : lightTheme),
			},
			{
				effects: this.#syntaxConf.reconfigure(isDark ? darkHighlighting : lightHighlighting),
			},
		);
	}

	focus() {
		this.#editor.focus();
	}

	static Theme = Object.freeze({
		LIGHT: Symbol('light'),
		DARK: Symbol('dark'),
	});

	static Language = Object.freeze({
		TEXT: Symbol('text'),
		HTML: Symbol('html'),
		JAVASCRIPT: Symbol('javascript'),
		CSS: Symbol('css'),
		JAVA: Symbol('java'),
	});
}
